import React, { useState, useEffect, useCallback } from 'react';

import {
  getBlogPosts,
  postBlogPost,
  editBlogPost,
  deleteBlogPost,
} from '../../../api/services/blogPostsApi';

import {
  createComment,
  updateComment,
  deleteComment,
} from '../../../api/services/blogPostCommentsApi';

import ContentChangeButton from '../../../components/admin/buttons/ContentChangeButton/ContentChangeButton';
import AdminBlogPostCard from '../../../components/admin/cards/AdminBlogPostCard/AdminBlogPostCard';
import Pagination from '../../../components/shared/Pagination/Pagination';
import BlogPostModal from '../../../components/admin/modals/BlogPostModal/BlogPostModal';
import BlogPostCommentModal from '../../../components/admin/modals/BlogPostCommentModal/BlogPostCommentModal';
import ConfirmDeleteModal from '../../../components/admin/modals/ConfirmDeleteModal/ConfirmDeleteModal';

import './AdminBlog.css';

const AdminBlog = ({ scrollContainerRef }) => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [totalPosts, setTotalPosts] = useState(0);

  const [selectedBlogPost, setSelectedBlogPost] = useState(null);
  const [isBlogPostEditModalOpen, setIsBlogPostEditModalOpen] = useState(false);
  const [isBlogPostDeleteModalOpen, setIsBlogPostDeleteModalOpen] =
    useState(false);
  const [blogPostEditModalMode, setBlogPostEditModalMode] = useState('edit');

  const [selectedComment, setSelectedComment] = useState(null);
  const [isCommentEditModalOpen, setIsCommentEditModalOpen] = useState(false);
  const [isCommentDeleteModalOpen, setIsCommentDeleteModalOpen] =
    useState(false);
  const [commentEditModalMode, setCommentEditModalMode] = useState('edit');
  const [commentEditModalError, setCommentEditModalError] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  /* ---------- Fetch Blog Posts ---------- */

  const getBlogPostsForCurrentPage = useCallback(async () => {
    try {
      const res = await getBlogPosts({
        page: currentPage,
        limit: postsPerPage,
      });

      setBlogPosts(res.data);
      setTotalPosts(res.total);
    } catch (err) {
      console.log('Failed to fetch blog posts:', err);
    }
  }, [currentPage, postsPerPage]);

  useEffect(() => {
    getBlogPostsForCurrentPage();
  }, [getBlogPostsForCurrentPage]);

  /* ---------- Scroll ---------- */

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (scrollContainerRef?.current) {
        scrollContainerRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [currentPage, scrollContainerRef]);

  /* ---------- Blog post handlers ---------- */

  const onBlogPostAddClickHandler = () => {
    setSelectedBlogPost({});
    setSelectedComment(null);
    setBlogPostEditModalMode('add');
    setIsBlogPostEditModalOpen(true);

    setIsBlogPostDeleteModalOpen(false);
    setIsCommentEditModalOpen(false);
    setIsCommentDeleteModalOpen(false);
  };

  const onBlogPostEditClickHandler = (blogPost) => {
    setSelectedBlogPost(blogPost);
    setSelectedComment(null);
    setBlogPostEditModalMode('edit');
    setIsBlogPostEditModalOpen(true);

    setIsBlogPostDeleteModalOpen(false);
    setIsCommentEditModalOpen(false);
    setIsCommentDeleteModalOpen(false);
  };

  const onBlogPostDeleteClickHandler = (blogPost) => {
    setSelectedBlogPost(blogPost);
    setSelectedComment(null);
    setIsBlogPostDeleteModalOpen(true);

    setIsBlogPostEditModalOpen(false);
    setIsCommentEditModalOpen(false);
    setIsCommentDeleteModalOpen(false);
  };

  const onBlogPostModalClose = () => {
    setIsBlogPostEditModalOpen(false);
    setSelectedBlogPost(null);
    setSelectedComment(null);
  };

  const onBlogPostDeleteModalClose = () => {
    setIsBlogPostDeleteModalOpen(false);
    setSelectedBlogPost(null);
    setSelectedComment(null);
  };

  const handleBlogPostSubmit = async (formData) => {
    try {
      if (blogPostEditModalMode === 'edit') {
        await editBlogPost(selectedBlogPost._id, formData);
        alert('Blog post updated successfully!');
      } else {
        await postBlogPost(formData);
        alert('Blog post created successfully!');
      }

      await getBlogPostsForCurrentPage();
      onBlogPostModalClose();
    } catch (err) {
      console.error(err);
      alert('Something went wrong.');
    }
  };

  const onBlogPostConfirmDelete = async () => {
    try {
      await deleteBlogPost(selectedBlogPost._id);
      await getBlogPostsForCurrentPage();

      onBlogPostDeleteModalClose();
      alert(`Deleted! ${selectedBlogPost.title} deleted successfully!`);
    } catch (err) {
      console.error(err);
      alert('Failed to delete blog post.');
    }
  };

  /* ---------- Comment handlers ---------- */

  const onCommentAddClickHandler = (blogPost) => {
    setSelectedBlogPost(blogPost);
    setSelectedComment({});
    setCommentEditModalMode('add');
    setIsCommentEditModalOpen(true);
    setCommentEditModalError('');

    setIsCommentDeleteModalOpen(false);
    setIsBlogPostEditModalOpen(false);
    setIsBlogPostDeleteModalOpen(false);
  };

  const onCommentEditClickHandler = (blogPost, comment) => {
    setSelectedBlogPost(blogPost);
    setSelectedComment(comment);
    setCommentEditModalMode('edit');
    setIsCommentEditModalOpen(true);
    setCommentEditModalError('');

    setIsCommentDeleteModalOpen(false);
    setIsBlogPostEditModalOpen(false);
    setIsBlogPostDeleteModalOpen(false);
  };

  const onCommentDeleteClickHandler = (blogPost, comment) => {
    setSelectedBlogPost(blogPost);
    setSelectedComment(comment);
    setIsCommentDeleteModalOpen(true);

    setIsCommentEditModalOpen(false);
    setIsBlogPostEditModalOpen(false);
    setIsBlogPostDeleteModalOpen(false);
  };

  const onCommentModalClose = () => {
    setIsCommentEditModalOpen(false);
    setSelectedBlogPost(null);
    setSelectedComment(null);
    setCommentEditModalError('');
  };

  const onCommentDeleteModalClose = () => {
    setIsCommentDeleteModalOpen(false);
    setSelectedBlogPost(null);
    setSelectedComment(null);
  };

  const handleCommentSubmit = async (formData) => {
    try {
      setCommentEditModalError('');

      if (commentEditModalMode === 'edit') {
        await updateComment(
          selectedBlogPost._id,
          selectedComment._id,
          formData,
        );
        alert('Comment updated successfully!');
      } else {
        await createComment(selectedBlogPost._id, formData);
        alert('Comment created successfully!');
      }

      await getBlogPostsForCurrentPage();
      onCommentModalClose();
    } catch (err) {
      console.error(err);

      setCommentEditModalError(
        err.message || 'Something went wrong. Please try again.',
      );
    }
  };

  const onCommentConfirmDelete = async () => {
    try {
      await deleteComment(selectedBlogPost._id, selectedComment._id);

      await getBlogPostsForCurrentPage();

      onCommentDeleteModalClose();
      alert(`Deleted! ${selectedComment.name} deleted successfully!`);
    } catch (err) {
      console.error(err);
      alert('Failed to delete comment.');
    }
  };

  /* ---------- Render ---------- */

  return (
    <div className='blog-page-container'>
      <div className='blog__page'>
        <div className='admin-blog-title-add-blog-post-container'>
          <h1 className='admin-blog-title'>Blog Posts</h1>

          <div className='admin-blog-post-count-add-blog-post-container'>
            {`Post count: ${totalPosts}`}
            <ContentChangeButton
              iconType='add'
              size='large'
              width='long'
              onClickHandler={onBlogPostAddClickHandler}
            >
              Add Blog Post
            </ContentChangeButton>
          </div>
        </div>

        <div className='blog__layout'>
          {blogPosts.map((b) => (
            <AdminBlogPostCard
              key={b._id}
              blogPost={b}
              onBlogPostEditClickHandler={onBlogPostEditClickHandler}
              onBlogPostDeleteClickHandler={onBlogPostDeleteClickHandler}
              onCommentAddClickHandler={onCommentAddClickHandler}
              onCommentEditClickHandler={onCommentEditClickHandler}
              onCommentDeleteClickHandler={onCommentDeleteClickHandler}
            />
          ))}
        </div>

        {!!totalPosts && (
          <Pagination
            totalPosts={totalPosts}
            postsPerPage={postsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>

      {/* Blog Post Modals */}
      {isBlogPostEditModalOpen && (
        <BlogPostModal
          onClose={onBlogPostModalClose}
          onSubmit={handleBlogPostSubmit}
          blogPost={selectedBlogPost}
          mode={blogPostEditModalMode}
        />
      )}

      {isBlogPostDeleteModalOpen && (
        <ConfirmDeleteModal
          onClose={onBlogPostDeleteModalClose}
          onConfirm={onBlogPostConfirmDelete}
          deleteItemTitle={selectedBlogPost?.title}
          deleteItemMsg={
            !!selectedBlogPost?.comments.length &&
            `This will also delete ${
              selectedBlogPost?.comments.length
            } associated comment${
              selectedBlogPost?.comments.length > 1 ? 's' : ''
            }.`
          }
        />
      )}

      {/* Comment Modals */}
      {isCommentEditModalOpen && (
        <BlogPostCommentModal
          onClose={onCommentModalClose}
          onSubmit={handleCommentSubmit}
          comment={selectedComment}
          mode={commentEditModalMode}
          errorMsg={commentEditModalError}
        />
      )}

      {isCommentDeleteModalOpen && (
        <ConfirmDeleteModal
          onClose={onCommentDeleteModalClose}
          onConfirm={onCommentConfirmDelete}
          deleteItemTitle={selectedComment?.name}
        />
      )}
    </div>
  );
};

export default AdminBlog;
